import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type PWAState =
  | 'unsupported'   // przeglądarka nie wspiera instalacji
  | 'installed'     // już zainstalowano (standalone mode)
  | 'ios'           // iOS — manual install via Share menu
  | 'ready'         // prompt gotowy do wywołania
  | 'dismissed'     // użytkownik odrzucił wcześniej
  | 'installing';   // w trakcie instalacji

export function usePWAInstall() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [state, setState] = useState<PWAState>('unsupported');

  useEffect(() => {
    // Sprawdź czy już działa w trybie standalone (zainstalowana)
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as { standalone?: boolean }).standalone === true;

    if (isStandalone) {
      setState('installed');
      return;
    }

    // iOS — Safari nie obsługuje beforeinstallprompt
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /safari/i.test(navigator.userAgent) && !/chrome/i.test(navigator.userAgent);
    if (isIOS || isSafari) {
      setState('ios');
      return;
    }

    // Sprawdź localStorage — czy użytkownik już odrzucił
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      setState('dismissed');
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
      setState('ready');
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Nasłuchuj na zakończoną instalację
    const installed = () => setState('installed');
    window.addEventListener('appinstalled', installed);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const install = useCallback(async () => {
    if (!prompt) return false;
    setState('installing');
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') {
      setState('installed');
      setPrompt(null);
      return true;
    } else {
      setState('dismissed');
      localStorage.setItem('pwa-install-dismissed', '1');
      return false;
    }
  }, [prompt]);

  const resetDismissed = useCallback(() => {
    localStorage.removeItem('pwa-install-dismissed');
    if (prompt) setState('ready');
  }, [prompt]);

  return { state, install, resetDismissed };
}
